import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/common/SkeletonLoader';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface ColorCodingThresholds {
  green: number;  // rate >= green → green
  yellow: number; // rate >= yellow → yellow, else red
}

export interface KpiCardProps {
  /** The primary metric value to display */
  value: string | number | null;
  /** Label displayed above the value */
  label: string;
  /** Unit suffix (e.g., '%', 'days') */
  unit?: string;
  /** Trend value (positive = improving, negative = declining) */
  trend?: number | null;
  /** Thresholds for color-coding the value */
  colorCoding?: ColorCodingThresholds;
  /** The numeric rate used for color coding (defaults to value if numeric) */
  colorCodingValue?: number | null;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Click handler for drill-down */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Determine the color class based on value and thresholds.
 */
function getColorClass(
  value: number | null | undefined,
  thresholds?: ColorCodingThresholds
): string {
  if (!thresholds || value == null) return 'text-[#111111]';
  if (value >= thresholds.green) return 'text-[#1A7A3C]';
  if (value >= thresholds.yellow) return 'text-[#92400E]';
  return 'text-[#B91C1C]';
}

/**
 * Render the trend indicator with semantic colors.
 */
function TrendIndicator({ trend }: { trend: number }) {
  if (trend > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#1A7A3C]">
        <TrendingUp className="h-4 w-4" aria-hidden="true" />
        +{trend.toFixed(1)}
      </span>
    );
  }
  if (trend < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#B91C1C]">
        <TrendingDown className="h-4 w-4" aria-hidden="true" />
        {trend.toFixed(1)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#555555]">
      <Minus className="h-4 w-4" aria-hidden="true" />
      0.0
    </span>
  );
}

/**
 * Shared KPI card component for the analytics dashboard.
 * Displays a headline metric with optional trend, color coding, and click-through.
 */
export function KpiCard({
  value,
  label,
  unit,
  trend,
  colorCoding,
  colorCodingValue,
  loading = false,
  error = null,
  onClick,
  className,
}: KpiCardProps) {
  if (loading) {
    return (
      <div
        className={cn(
          'bg-white border border-neutral-200 rounded-lg p-5',
          className
        )}
        aria-busy="true"
      >
        <Skeleton className="h-3 w-20 mb-3" />
        <Skeleton className="h-8 w-24 mb-2" />
        <Skeleton className="h-4 w-16" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          'bg-white border border-neutral-200 rounded-lg p-5',
          className
        )}
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#555555] mb-1">
          {label}
        </p>
        <p className="text-sm text-[#B91C1C]">{error}</p>
      </div>
    );
  }

  const numericValue =
    colorCodingValue ?? (typeof value === 'number' ? value : null);
  const valueColorClass = getColorClass(numericValue, colorCoding);
  const displayValue = value != null ? value : '—';

  return (
    <div
      className={cn(
        'bg-white border border-neutral-200 rounded-lg p-5',
        onClick && 'cursor-pointer hover:border-[#6B4FBB] transition-colors',
        className
      )}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={
        onClick ? `${label}: ${displayValue}${unit ? ` ${unit}` : ''}. Click for details.` : undefined
      }
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#555555] mb-1">
        {label}
      </p>
      <div className="flex items-baseline gap-1">
        <span className={cn('text-[28px] font-bold leading-tight', valueColorClass)}>
          {displayValue}
        </span>
        {unit && (
          <span className="text-[13px] font-medium text-[#9A9A9A]">{unit}</span>
        )}
      </div>
      {trend != null && (
        <div className="mt-2">
          <TrendIndicator trend={trend} />
        </div>
      )}
    </div>
  );
}

export default KpiCard;
